import { v4 as uuid } from 'uuid';

import { AGUITransport } from 'modules/shared/libs/chat/transports/AGUITransport';

import type { Message as AGUIMessage } from '@ag-ui/core';
import type { ChatUIMessage } from 'modules/shared/libs/chat/types';

type ResearchTransportOptions = {
  api: string;
  credentials?: RequestCredentials;
  disabledSources?: string[];
};

export class ResearchTransport extends AGUITransport<ChatUIMessage> {
  private disabledSources?: string[];

  constructor(options: ResearchTransportOptions) {
    super({
      api: options.api,
      credentials: options.credentials,
    });
    this.disabledSources = options.disabledSources;
  }

  setDisabledSources(disabledSources: string[] | undefined) {
    this.disabledSources = disabledSources;
  }

  override sendMessages = async ({
    chatId,
    messages,
    abortSignal,
  }: {
    chatId: string;
    messages: ChatUIMessage[];
    abortSignal?: AbortSignal;
  }) => {
    const latestUserMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'user');
    const messagesToSend = latestUserMessage ? [latestUserMessage] : [];

    // Assign UUIDs to messages without IDs (same as base AGUITransport)
    messagesToSend.forEach((msg) => {
      if (!msg.id) {
        msg.id = uuid();
      }
    });

    const aguiMessages = this.convertToAGUIMessages(messagesToSend);

    const requestBody: {
      thread_id: string;
      run_id: string;
      state: {
        thread_type: 'RESEARCH';
      };
      messages: AGUIMessage[];
      disabled_sources?: string[];
    } = {
      thread_id: chatId,
      run_id: crypto.randomUUID(),
      state: {
        thread_type: 'RESEARCH',
      },
      messages: aguiMessages,
      ...(this.disabledSources &&
        this.disabledSources.length > 0 && {
          disabled_sources: this.disabledSources,
        }),
    };

    const response = await fetch(this.api, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(requestBody),
      credentials: this.credentials,
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is not readable');
    }

    return this.createAISDKStream(response.body);
  };
}
